import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAntiCheat } from '../hooks/useAntiCheat';
import { useExamTimer } from '../hooks/useExamTimer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { 
  Clock, 
  AlertTriangle, 
  Send, 
  ChevronRight, 
  ChevronLeft,
  Loader2,
  ShieldAlert,
  Key,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { renderSafeHtml } from '../components/RichTextEditor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

// Generate or get device ID from localStorage
const getDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
};

const TakeExam = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { student, token, isStudent, isApprovedStudent } = useAuth();
  
  const [exam, setExam] = useState(null);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [examEnded, setExamEnded] = useState(false);
  const [endReason, setEndReason] = useState('');
  
  // Access code state
  const [requiresAccessCode, setRequiresAccessCode] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [accessCodeError, setAccessCodeError] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [maxViolations, setMaxViolations] = useState(2);
  
  const autoSaveRef = useRef(null);
  const timeUpHandledRef = useRef(false);
  // Always the latest answers: save/submit also run from callbacks created earlier.
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Anti-cheat hook with configurable max violations
  const handleViolation = useCallback((type, count) => {
    if (type === 'terminated') {
      setExamEnded(true);
      setEndReason('تم إنهاء الامتحان بسبب تكرار مغادرة شاشة الامتحان.');
    }
  }, []);

  // Student info for watermark
  const studentInfo = {
    name: student?.full_name || student?.name || 'طالب',
    device_id: getDeviceId()
  };

  const {
    violationCount,
    showWarning,
    isTerminated,
    warningMessage,
    dismissWarning,
    syncViolationCount,
  } = useAntiCheat(session?.id, !examEnded && !!session, handleViolation, maxViolations, studentInfo);

  // Timer hook
  const handleTimeUp = useCallback(async () => {
    if (session && !examEnded && !timeUpHandledRef.current) {
      timeUpHandledRef.current = true;
      setExamEnded(true);
      setEndReason('انتهى وقت الاختبار');
      await submitExam();
    }
  }, [session, examEnded]);

  // Run the countdown on server time so a wrong device clock can't shift it.
  const timerStart = useMemo(() => {
    if (!session?.started_at) return null;
    return new Date(Date.parse(session.started_at) + (session.clockOffsetMs || 0)).toISOString();
  }, [session]);

  const {
    formattedTime,
    isTimeUp,
    isWarning: isTimeWarning,
    isCritical: isTimeCritical,
    percentageRemaining,
  } = useExamTimer(exam?.duration_minutes, timerStart, handleTimeUp);

  const canSubmit = exam?.allow_early_submit !== false || isTimeUp;

  // Check authentication and approval
  useEffect(() => {
    if (!isStudent) {
      navigate('/student/login');
      return;
    }
    
    if (!isApprovedStudent) {
      navigate('/student/pending');
      return;
    }
    
    startExam();
  }, [isStudent, isApprovedStudent, examId]);

  // Auto-save answers every 30 seconds (not restarted by new answers)
  useEffect(() => {
    if (session && !examEnded) {
      autoSaveRef.current = setInterval(() => {
        saveAnswers();
      }, 30000);

      return () => {
        if (autoSaveRef.current) {
          clearInterval(autoSaveRef.current);
        }
      };
    }
  }, [session, examEnded]);

  // Poll for session status (check if blocked by teacher)
  useEffect(() => {
    if (!session || examEnded) return;
    
    const checkSessionStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/student/exam-sessions/${session.id}/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'blocked') {
            setExamEnded(true);
            setEndReason(data.blocked_reason || 'تم إيقاف الاختبار من قبل المعلم');
          } else if (data.status === 'completed') {
            // Finalized by the server (e.g. this device's timer fell behind).
            setExamEnded(true);
            setEndReason('انتهى وقت الاختبار');
          }
        }
      } catch (error) {
        console.error('Error checking session status:', error);
      }
    };
    
    const statusInterval = setInterval(checkSessionStatus, 5000); // Check every 5 seconds
    
    return () => clearInterval(statusInterval);
  }, [session, examEnded, token, API_URL]);

  // Handle terminated state
  useEffect(() => {
    if (isTerminated && !examEnded) {
      setExamEnded(true);
      submitExam();
    }
  }, [isTerminated]);

  const startExam = async (codeToUse = null) => {
    setLoading(true);
    try {
      // Fetch exam
      const examResponse = await fetch(`${API_URL}/api/student/exam/${examId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!examResponse.ok) {
        const error = await examResponse.json();
        if (examResponse.status === 403) {
          toast.error('حسابك قيد المراجعة');
          navigate('/student/pending');
          return;
        }
        toast.error(error.detail || 'فشل تحميل الاختبار');
        navigate('/student/exams');
        return;
      }
      
      const examData = await examResponse.json();
      setExam(examData);
      
      // Store max violations from exam
      setMaxViolations(examData.max_violations || 2);
      
      // Check if exam requires access code
      if (examData.require_access_code && !codeToUse) {
        setRequiresAccessCode(true);
        setLoading(false);
        return;
      }

      // Start session with device ID and optional access code
      const deviceId = getDeviceId();
      const sessionResponse = await fetch(`${API_URL}/api/exam-sessions/start?exam_id=${examId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_code: codeToUse || null,
          device_id: deviceId
        }),
      });
      
      if (!sessionResponse.ok) {
        const error = await sessionResponse.json();
        if (sessionResponse.status === 403) {
          if (error.detail?.includes('جهاز آخر')) {
            toast.error('لا يمكن الوصول للاختبار من جهاز آخر');
          } else {
            toast.error('حسابك قيد المراجعة');
            navigate('/student/pending');
          }
          return;
        }
        if (sessionResponse.status === 400) {
          if (error.detail?.includes('رمز الدخول')) {
            setAccessCodeError(error.detail);
            setRequiresAccessCode(true);
            setLoading(false);
            return;
          }
          if (error.detail?.includes('استنفدت')) {
            toast.error(error.detail);
            navigate('/student/exams');
            return;
          }
        }
        toast.error(error.detail || 'فشل بدء الاختبار');
        navigate('/student/exams');
        return;
      }
      
      const sessionData = await sessionResponse.json();
      setSession({
        ...sessionData,
        clockOffsetMs: sessionData.server_now ? Date.now() - Date.parse(sessionData.server_now) : 0,
      });
      setRequiresAccessCode(false);
      
      // Store max violations from session
      if (sessionData.max_violations) {
        setMaxViolations(sessionData.max_violations);
      }

      // Use questions from session (for smart question bank) or from exam
      if (sessionData.questions && sessionData.questions.length > 0) {
        setExam(prevExam => ({
          ...prevExam,
          questions: sessionData.questions
        }));
      }

      // Initialize answers from session if exists
      if (sessionData.answers && sessionData.answers.length > 0) {
        const savedAnswers = {};
        sessionData.answers.forEach(a => {
          savedAnswers[a.question_id] = a.answer;
        });
        setAnswers(savedAnswers);
      }

    } catch (error) {
      console.error('Error starting exam:', error);
      toast.error('حدث خطأ أثناء بدء الاختبار');
      navigate('/student/exams');
    }
    setLoading(false);
  };

  const saveAnswers = async () => {
    if (!session || examEnded) return;

    try {
      await fetch(`${API_URL}/api/exam-sessions/save-answers`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: session.id,
          answers: getAnswersArray(),
        }),
      });
    } catch (error) {
      console.error('Error saving answers:', error);
    }
  };

  const getAnswersArray = () => Object.entries(answersRef.current).map(([question_id, answer]) => ({
    question_id,
    answer,
  }));

  const showResults = () => navigate(`/student/exam/${examId}/results/${session.id}`);

  const submitExam = async () => {
    if (!session || submitting) return;

    setSubmitting(true);
    try {
      // Submit exam together with the final answers
      const response = await fetch(`${API_URL}/api/exam-sessions/${session.id}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers: getAnswersArray() }),
      });

      if (response.ok) {
        toast.success('تم تسليم الاختبار بنجاح');
        // Same as the question bank: land on the result/review page.
        showResults();
      } else {
        const error = await response.json().catch(() => ({}));
        if (timeUpHandledRef.current && error.detail?.includes('منتهي مسبقاً')) {
          showResults();
        } else {
          toast.error(error.detail || 'فشل تسليم الاختبار');
        }
      }
    } catch (error) {
      console.error('Error submitting exam:', error);
      toast.error('حدث خطأ أثناء تسليم الاختبار');
    }
    setSubmitting(false);
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const goToQuestion = (index) => {
    if (index >= 0 && index < exam.questions.length) {
      setCurrentQuestion(index);
    }
  };

  const getQuestionTypeName = (type) => {
    const types = {
      multiple_choice: 'اختيار من متعدد',
      true_false: 'صح أو خطأ',
      short_answer: 'إجابة قصيرة',
      paragraph: 'مقالي',
      essay: 'مقالي',
    };
    return types[type] || type;
  };

  // Handle access code submission
  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) {
      setAccessCodeError('يرجى إدخال رمز الدخول');
      return;
    }
    
    setVerifyingCode(true);
    setAccessCodeError('');
    
    // Try to start exam with the code
    await startExam(accessCode.toUpperCase());
    setVerifyingCode(false);
  };

  // Access Code Entry Screen
  if (requiresAccessCode && !session) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#FFFFFF]/80 border border-[#E8DFCF] shadow-lg">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#EF9F27]/10 border border-[#E8DFCF] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-[#EF9F27]" />
            </div>
            <CardTitle className="text-xl font-bold text-[#412402]">
              رمز الدخول مطلوب
            </CardTitle>
            <CardDescription className="text-[#412402]/60">
              يرجى إدخال رمز الدخول الذي حصلت عليه من المعلم
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAccessCodeSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="access-code" className="text-[#EF9F27]">رمز الدخول</Label>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#EF9F27]/50" />
                  <Input
                    id="access-code"
                    type="text"
                    placeholder="XXXXXXXX"
                    value={accessCode}
                    className="pr-10 bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] text-center text-xl tracking-widest font-mono"
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    maxLength={8}
                    dir="ltr"
                    data-testid="access-code-input"
                  />
                </div>
                {accessCodeError && (
                  <p className="text-red-500 text-sm">{accessCodeError}</p>
                )}
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] font-bold"
                disabled={verifyingCode}
                data-testid="verify-code-btn"
              >
                {verifyingCode ? (
                  <>
                    <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                    جاري التحقق...
                  </>
                ) : (
                  'دخول الاختبار'
                )}
              </Button>
              
              <Button 
                type="button"
                variant="outline"
                className="w-full border-[#E8DFCF] text-[#EF9F27] hover:bg-[#EF9F27]/10"
                onClick={() => navigate('/student/exams')}
              >
                العودة
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#EF9F27] mx-auto mb-4" />
          <p className="text-[#412402]/60">جاري تحميل الاختبار...</p>
        </div>
      </div>
    );
  }

  if (examEnded) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#FFFFFF]/80 border border-[#E8DFCF]">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-[#412402] mb-2">انتهى الاختبار</h2>
            <p className="text-[#412402]/60 mb-6">{endReason}</p>
            {session?.id && (
              <Button
                variant="outline"
                onClick={() => navigate(`/student/exam/${examId}/results/${session.id}`)}
                className="w-full mb-3 border-[#EF9F27] text-[#EF9F27] hover:bg-[#EF9F27]/10"
                data-testid="ended-view-results-btn"
              >
                عرض النتيجة والمراجعة
              </Button>
            )}
            <Button
              onClick={() => navigate('/student/exams')}
              className="bg-[#EF9F27] text-[#854F0B] font-bold"
            >
              العودة للاختبارات
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const question = exam?.questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;
  const progressPercentage = (answeredCount / exam?.questions.length) * 100;

  return (
    <div className="min-h-screen bg-[#FFFFFF] exam-mode relative">
      {/* Watermark for Screenshot Protection */}
      <div className="fixed inset-0 pointer-events-none z-[5] overflow-hidden select-none" aria-hidden="true">
        <div className="absolute inset-0 flex flex-wrap justify-center items-center gap-32 -rotate-45 opacity-[0.03]">
          {[...Array(20)].map((_, i) => (
            <span key={i} className="text-2xl font-bold text-[#412402] whitespace-nowrap">
              {student?.name} - {new Date().toLocaleDateString('ar-SA')}
            </span>
          ))}
        </div>
      </div>

      {/* Warning Overlay */}
      {showWarning && (
        <div className="fixed inset-0 z-[100] bg-[#FFFFFF]/95 backdrop-blur-sm flex items-center justify-center p-4">
          <Card className="max-w-md w-full bg-[#FFFFFF] border border-[#E8DFCF] shadow-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-[#EF9F27]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldAlert className="w-8 h-8 text-[#EF9F27]" />
              </div>
              <h2 className="text-xl font-bold text-[#412402] mb-4">تحذير!</h2>
              <p className="text-[#412402]/80 text-lg leading-relaxed mb-6 whitespace-pre-line">{warningMessage}</p>
              <Button 
                onClick={dismissWarning}
                className="bg-[#EF9F27] text-[#854F0B] font-bold w-full"
                data-testid="dismiss-warning-btn"
              >
                فهمت، متابعة الاختبار
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Timer */}
      <div 
        className={`fixed top-4 left-4 z-50 bg-[#FFFFFF]/90 rounded-full px-6 py-2 shadow-lg border flex items-center gap-2 ${
          isTimeCritical ? 'animate-pulse bg-red-500/20 border-red-500/30' : 
          isTimeWarning ? 'bg-[#EF9F27]/10 border-[#E8DFCF]' : 'border-[#E8DFCF]'
        }`}
        data-testid="exam-timer"
      >
        <Clock className={`w-5 h-5 ${isTimeCritical ? 'text-red-400' : isTimeWarning ? 'text-[#EF9F27]' : 'text-[#EF9F27]'}`} />
        <span className={`font-mono text-lg font-bold ${
          isTimeCritical ? 'text-red-400' : isTimeWarning ? 'text-[#EF9F27]' : 'text-[#412402]'
        }`}>
          {formattedTime}
        </span>
      </div>

      {/* Violation Counter */}
      {violationCount > 0 && (
        <div className="fixed top-4 right-4 z-50 bg-red-100 text-red-700 rounded-full px-4 py-2 text-sm font-medium">
          مخالفات: {violationCount}
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 pt-20">
        {/* Exam Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#412402] mb-2">{exam?.title}</h1>
          <div className="flex items-center gap-4 text-sm text-[#412402]/60">
            <span>السؤال {currentQuestion + 1} من {exam?.questions.length}</span>
            <span>•</span>
            <span>تم الإجابة على {answeredCount} من {exam?.questions.length}</span>
          </div>
          <Progress value={progressPercentage} className="mt-3 h-2" />
        </div>

        {/* Question Card */}
        <Card className="border-[#E8DFCF] mb-6">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-10 h-10 bg-[#EF9F27]/15 text-[#EF9F27] rounded-lg flex items-center justify-center font-bold">
                {currentQuestion + 1}
              </span>
              <span className="text-sm bg-[#FFFFFF]/50 text-[#412402]/60 px-3 py-1 rounded-full">
                {getQuestionTypeName(question?.question_type)}
              </span>
              <span className="text-sm text-[#412402]/50 mr-auto"></span>
            </div>

            <h2
              dir="auto"
              className="text-lg font-semibold text-[#412402] mb-6 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={renderSafeHtml(question?.text || '')}
            />

            {/* Multiple Choice */}
            {question?.question_type === 'multiple_choice' && (
              <RadioGroup
                dir="rtl"
                value={answers[question.id] || ''}
                onValueChange={(value) => handleAnswerChange(question.id, value)}
                className="space-y-3"
              >
                {question.options?.map((option, index) => (
                  <label
                    key={option.id}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      answers[question.id] === option.id
                        ? 'border-[#d4a853] bg-[#EF9F27]/10'
                        : 'border-[#E8DFCF] hover:border-[#E8DFCF] hover:bg-[#FFFFFF]'
                    }`}
                    data-testid={`option-${index}`}
                  >
                    <RadioGroupItem value={option.id} id={option.id} />
                    <span
                      dir="auto"
                      className="text-[#412402]"
                      dangerouslySetInnerHTML={renderSafeHtml(option.text || '')}
                    />
                  </label>
                ))}
              </RadioGroup>
            )}

            {/* True/False */}
            {question?.question_type === 'true_false' && (
              <RadioGroup
                dir="rtl"
                value={answers[question.id] || ''}
                onValueChange={(value) => handleAnswerChange(question.id, value)}
                className="flex gap-4"
              >
                <label
                  className={`flex-1 flex items-center justify-center gap-2 p-6 rounded-lg border cursor-pointer transition-all ${
                    answers[question.id] === 'true'
                      ? 'border-[#d4a853] bg-[#EF9F27]/10'
                      : 'border-[#E8DFCF] hover:border-[#E8DFCF] hover:bg-[#FFFFFF]'
                  }`}
                  data-testid="option-true"
                >
                  <RadioGroupItem value="true" id="true" />
                  <span className="text-lg font-medium text-[#412402]">صح</span>
                </label>
                <label
                  className={`flex-1 flex items-center justify-center gap-2 p-6 rounded-lg border cursor-pointer transition-all ${
                    answers[question.id] === 'false'
                      ? 'border-[#d4a853] bg-[#EF9F27]/10'
                      : 'border-[#E8DFCF] hover:border-[#E8DFCF] hover:bg-[#FFFFFF]'
                  }`}
                  data-testid="option-false"
                >
                  <RadioGroupItem value="false" id="false" />
                  <span className="text-lg font-medium text-[#412402]">خطأ</span>
                </label>
              </RadioGroup>
            )}

            {/* Short Answer */}
            {question?.question_type === 'short_answer' && (
              <Input
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                placeholder="ادخل إجابتك هنا..."
                className="text-lg"
                data-testid="short-answer-input"
              />
            )}

            {/* Paragraph */}
            {question?.question_type === 'paragraph' && (
              <Textarea
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                placeholder="اكتب إجابتك هنا..."
                rows={8}
                className="text-base leading-relaxed"
                data-testid="paragraph-input"
              />
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={() => goToQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 0}
            data-testid="prev-question-btn"
          >
            <ChevronRight className="w-5 h-5 ml-1" />
            السابق
          </Button>

          {/* Question Dots */}
          <div className="hidden sm:flex items-center gap-1 flex-wrap justify-center">
            {exam?.questions.map((q, index) => (
              <button
                key={index}
                onClick={() => goToQuestion(index)}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                  currentQuestion === index
                    ? 'bg-[#EF9F27] text-[#412402]'
                    : answers[q.id]
                    ? 'bg-[#EF9F27]/15 text-[#EF9F27]'
                    : 'bg-[#FFFFFF]/50 text-[#412402]/60 hover:bg-[#EF9F27]/15'
                }`}
                data-testid={`question-dot-${index}`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {currentQuestion === exam?.questions.length - 1 ? (
            <Button
              onClick={() => setSubmitDialogOpen(true)}
              className="bg-[#EF9F27] hover:bg-[#D88E1F]"
              disabled={!canSubmit}
              data-testid="submit-exam-btn"
            >
              {canSubmit ? <Send className="w-5 h-5 ml-1" /> : <Lock className="w-5 h-5 ml-1" />}
              {canSubmit ? 'تسليم الاختبار' : 'التسليم عند انتهاء الوقت'}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => goToQuestion(currentQuestion + 1)}
              data-testid="next-question-btn"
            >
              التالي
              <ChevronLeft className="w-5 h-5 mr-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد تسليم الاختبار</AlertDialogTitle>
            <AlertDialogDescription>
              {answeredCount < exam?.questions.length ? (
                <>
                  لم تجب على جميع الأسئلة بعد. أجبت على {answeredCount} من {exam?.questions.length} سؤال.
                  <br />
                  هل أنت متأكد من تسليم الاختبار؟
                </>
              ) : (
                'هل أنت متأكد من تسليم الاختبار؟ لن تتمكن من تعديل إجاباتك بعد التسليم.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction 
              onClick={submitExam}
              className="bg-[#EF9F27] hover:bg-[#D88E1F]"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  جاري التسليم...
                </>
              ) : (
                'تسليم الاختبار'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TakeExam;
